import { APIFetchError } from "../lib/error";

type FetchLike = (request: Request, init?: RequestInit) => Promise<Response>;

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function withRetry(fetch: FetchLike, backoff: number[]) {
  return async function fetchWithRetry(source: Request, init?: RequestInit) {
    for (let retryIndex = 0; ; ) {
      const request = source.clone();
      const response = await fetch(request, init);
      if (response.status < 500) {
        return response;
      }

      const delay = backoff[retryIndex++];
      if (isFinite(delay)) {
        console.info(`Retry attempt ${retryIndex} in ${delay} ms`);
        await wait(delay);
      } else {
        throw new APIFetchError("Status Code", request, response);
      }
    }
  };
}

function parseRetry(response: Response) {
  const retryPrompt = response.headers.get("retry-after");
  if (retryPrompt) {
    const delay = parseFloat(retryPrompt);
    if (isFinite(delay) && delay > 0) {
      return delay * 1000;
    }
  }
  return 5000;
}

export async function rateLimited<T>(
  attempt: () => Promise<T>,
  maxAttempts = 5
): Promise<T> {
  for (let attemptCount = 1; ; attemptCount++) {
    try {
      return await attempt();
    } catch (error: unknown) {
      if (error instanceof APIFetchError) {
        const response = error.response;
        if (response.status === 429) {
          if (attemptCount >= maxAttempts) {
            throw new Error(`Max attempts ${maxAttempts} reached`);
          }

          const delay = parseRetry(response);
          console.info(`Retry attempt ${attemptCount} in ${delay} ms`);
          await wait(delay);
          continue;
        }
      }

      throw error;
    }
  }
}
